import { useRef, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { DOCUMENT_TYPE_LABELS, DocumentType } from '@/types/document';
import { cn } from '@/lib/utils';

interface DocumentTypeMultiSelectProps {
  value: DocumentType[];
  onChange: (value: DocumentType[]) => void;
  /**
   * Types that verify the legal_doc source for the current transaction. Omit
   * to render one flat list - a financing round spans several transaction
   * types at once, so no single "verifies" claim would hold for all of them.
   */
  acceptedTags?: Set<DocumentType>;
  id?: string;
  disabled?: boolean;
}

const ALL_DOCUMENT_TYPES = Object.values(DocumentType);

// The trigger is a fixed-height button, so badges beyond this spill outside it.
const MAX_TRIGGER_BADGES = 2;

interface DocumentTypeGroupProps {
  label?: string;
  options: DocumentType[];
  selected: Set<DocumentType>;
  onToggle: (type: DocumentType) => void;
}

function DocumentTypeGroup({
  label,
  options,
  selected,
  onToggle,
}: DocumentTypeGroupProps) {
  if (options.length === 0) {
    return null;
  }

  return (
    <div className="space-y-1">
      {label && (
        <p className="px-2 py-1 text-xs font-medium text-muted-foreground">
          {label}
        </p>
      )}

      {options.map((type) => (
        <label
          key={type}
          className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
        >
          <Checkbox
            checked={selected.has(type)}
            onCheckedChange={() => onToggle(type)}
          />

          <span className="flex-1">{DOCUMENT_TYPE_LABELS[type]}</span>

          {selected.has(type) && <Check className="h-4 w-4 text-primary" />}
        </label>
      ))}
    </div>
  );
}

export function DocumentTypeMultiSelect({
  value,
  onChange,
  acceptedTags,
  id,
  disabled,
}: DocumentTypeMultiSelectProps) {
  const selected = new Set(value);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [dialogBoundary, setDialogBoundary] = useState<Element | null>(null);

  // Keep the list inside the surrounding dialog instead of letting it hang
  // past the dialog edge. Outside a dialog this stays null and the viewport
  // is the boundary. `modal` on the Popover is what makes the wheel reach
  // this list at all - a Dialog blocks wheel events outside its own subtree.
  const captureBoundary = (nextOpen: boolean) => {
    if (!nextOpen) return;
    setDialogBoundary(triggerRef.current?.closest('[role="dialog"]') ?? null);
  };

  const toggle = (type: DocumentType) => {
    const next = new Set(selected);

    if (next.has(type)) {
      next.delete(type);
    } else {
      next.add(type);
    }

    // Preserve canonical enum order.
    onChange(ALL_DOCUMENT_TYPES.filter((t) => next.has(t)));
  };

  const typeGroups: DocumentTypeGroupProps[] = acceptedTags
    ? [
        {
          label: 'Recommended · verifies legal_doc',
          options: ALL_DOCUMENT_TYPES.filter((type) => acceptedTags.has(type)),
          selected,
          onToggle: toggle,
        },
        {
          label: "Other · context only (won't verify)",
          options: ALL_DOCUMENT_TYPES.filter((type) => !acceptedTags.has(type)),
          selected,
          onToggle: toggle,
        },
      ]
    : [{ options: ALL_DOCUMENT_TYPES, selected, onToggle: toggle }];

  return (
    <Popover modal onOpenChange={captureBoundary}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal',
            value.length === 0 && 'text-muted-foreground'
          )}
        >
          {value.length === 0 ? (
            'Select document types'
          ) : (
            <span className="flex min-w-0 items-center gap-1 overflow-hidden">
              {value.slice(0, MAX_TRIGGER_BADGES).map((type) => (
                <Badge key={type} variant="secondary" className="truncate">
                  {DOCUMENT_TYPE_LABELS[type]}
                </Badge>
              ))}

              {value.length > MAX_TRIGGER_BADGES && (
                <Badge variant="secondary" className="shrink-0">
                  +{value.length - MAX_TRIGGER_BADGES}
                </Badge>
              )}
            </span>
          )}

          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="scrollbar-always max-h-[var(--radix-popover-content-available-height)] w-[var(--radix-popover-trigger-width)] overflow-y-auto p-1"
        align="start"
        collisionPadding={12}
        collisionBoundary={dialogBoundary ?? []}
      >
        <div className="space-y-2">
          {typeGroups.map((group) => (
            <DocumentTypeGroup key={group.label ?? 'ungrouped'} {...group} />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
